import NorthIcon from "@mui/icons-material/North";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import Bowser from "bowser";
import { initializeApp as FirebaseApp } from "firebase/app";
import { deleteToken, getMessaging, getToken } from "firebase/messaging";
import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import type { Device } from "@storyboard/web-client/auth";
import BrowserErrorRefresh from "@/components/ModalForms/BrowserErrorRefresh";
import { client } from "@/config";
import ModalForm from "@/elements/ModalForm";
import { firebaseConfig, vapidKey } from "@/hooks/firebase/config";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import UseTimeouts from "@/hooks/useTimeouts";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { UserContext } from "@/models/StateProviders/userProvider.tsx";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import BasicButtonStyles from "@/theme/BasicButtonStyles";
import { isElectron } from "@/utils";

type NotificationTypes = "default" | "granted" | "denied";

export default function UseFirebase(): {
  BrowserCantGetTokenModal?: () => React.JSX.Element;
  ActivateFirebaseBanner?: () => React.JSX.Element;
  NotificationBanner?: () => React.JSX.Element;
  showNotificationsBanner?: boolean;
  deleteActiveToken?: () => Promise<void> | undefined;
  notificationsState?: NotificationTypes;
  requestDesktopNotifications?: () => Promise<void>;
} {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  // check if the browser supports service workers and notifications
  const checkForServiceWorker = () => {
    const ServiceWorkerIsSupported = "serviceWorker" in window.navigator;
    const NotificationIsSupported = !!window?.Notification;
    const hasNotifications = "Notification" in window;
    const hasServiceWorker = !!window?.navigator?.serviceWorker;

    return !(!hasNotifications || !NotificationIsSupported || !ServiceWorkerIsSupported || !hasServiceWorker);
  };

  const { setUserFeedbackModalOpen } = useContext(UxContext);
  const { normalButtonStyles, normalIconStyles } = BasicButtonStyles();
  const { ampli } = useContext(TrackingContext);

  if (isElectron) {
    return {
      BrowserCantGetTokenModal: () => <></>,
      ActivateFirebaseBanner: () => <></>,
      showNotificationsBanner: false,
      deleteActiveToken: () => undefined,
      notificationsState: "granted",
    };
  }

  if (!checkForServiceWorker()) {
    ampli.serviceworkerOfNotificationNotAvailable();

    // Return an empty component as a fallback if Notifications are not available
    return {
      showNotificationsBanner: true,
      ActivateFirebaseBanner: () => <></>,
      BrowserCantGetTokenModal: () => (
        <Button
          onClick={() => setUserFeedbackModalOpen(true)}
          sx={{
            ...normalButtonStyles,
            p: "1rem",
            fontSize: "14px",
            fontWeight: "normal",
            textAlign: "left",
            background: theme.palette.secondary.main,
          }}
        >
          Your browser does not support Notifications or ServiceWorkers. Please contact support for help.
        </Button>
      ),
      deleteActiveToken: async () => null,
      notificationsState: "granted",
    };
  }

  const { browserCantGetToken } = useGlobalFlags();
  const { myAccountId } = useContext(MyAccountContext);
  const browser = Bowser.getParser(window.navigator.userAgent);
  const [token, setToken] = useState<string | null>(null);
  const [fetchingToken, setFetchingToken] = useState<boolean>(false);
  const [requestNotification, setRequestNotification] = useState<boolean>(false);
  const [notificationsState, setNotificationsState] = useState<NotificationTypes>(Notification.permission);
  const [tokenCheck, setTokenCheck] = useState<number>(0);
  const [brokenBrowser, setBrokenBrowser] = useState<boolean>(false);
  const { refresh } = UseTimeouts(15);
  const friendlyName = `${browser.getBrowserName()} ${browser.getBrowserVersion()} ${browser.getOSName()} ${browser.getOSVersion()} ${browser.getPlatformType()}`;
  const isEdge = browser.getBrowserName().toLowerCase().includes("microsoft edge");
  const messaging = useRef(null);
  // Tracks which token has already been registered with the server so the
  // registration effect below does not fire twice for the same token.
  const registeredTokenRef = useRef<string | null>(null);
  FirebaseApp(firebaseConfig);
  messaging.current = getMessaging();

  useEffect(() => {
    const hook = (event: MessageEvent<any>) => {
      if (!event.data.action) {
        return;
      }

      switch (event.data.action) {
        case "redirect-from-notificationclick":
          window.open(event.data.url, "_self", "noreferrer");
          break;
        // no default
      }
    };
    navigator.serviceWorker.addEventListener("message", hook);

    return () => {
      navigator.serviceWorker.removeEventListener("message", hook);
    };
  }, []);

  const deleteActiveToken = async () => {
    if (token) {
      ampli.deleteToken({ token });
      await deleteToken(messaging.current);
      setToken(() => null);
      registeredTokenRef.current = null;
    }
  };

  /**
   * Check our server to see if the token is already registered
   * @param token
   */
  const fetchUserDevice = async (token: string): Promise<Device | undefined> =>
    user?.devices?.find((device) => device.deviceToken === token && device.enabled);

  /**
   * Send the firebase token to our server, so we know where to send notifications.
   * Skips if `myAccountId` is not yet available — the effect below will retry once
   * the account context resolves so the device still gets registered.
   * @param token
   */
  const sendTokenToServer = async (token: string): Promise<void> => {
    if (!myAccountId) {
      return;
    }
    const activeDevice = await fetchUserDevice(token);
    if (!activeDevice) {
      await client.setPushNotificationToken({
        accountId: myAccountId,
        deviceToken: token,
        friendlyName,
      });
    }
  };

  const asyncFetchToken = async () => {
    try {
      return await getToken(messaging.current, { vapidKey });
    } catch (error) {
      ampli.failedToRetrieveToken({ error });
      return;
    }
  };
  /**
   * Request a firebase token. The effect below picks up the new token and
   * registers it with our server once `myAccountId` is available.
   */
  const maxRetries = 10;
  const fetchFirebaseToken = async (retries: number): Promise<void> => {
    if (retries >= maxRetries) {
      ampli.failedToRetrieveToken({
        error: `Could not retrieve token after ${retries} retries`,
      });
      return;
    }
    if (token || fetchingToken) {
      return;
    }

    try {
      setFetchingToken(() => true);
      const tokenValue = await asyncFetchToken();
      if (tokenValue) {
        setToken(() => tokenValue);
        setFetchingToken(() => false);
        ampli.hasToken({ token: tokenValue });
      } else {
        await fetchFirebaseToken(retries + 1);
      }
    } catch (e) {
      await fetchFirebaseToken(retries + 1);
    }
  };

  /**
   * Request access to the browsers notification stack
   * if granted access, then request a firebase token. If the account context
   * has not resolved yet, the effect tied to `myAccountId` will trigger the
   * fetch (and registration) once it does.
   */
  const requestDesktopNotifications = async (): Promise<void> => {
    setRequestNotification(() => true);
    const permissions = await Notification.requestPermission();
    setNotificationsState(() => permissions);
    setRequestNotification(() => false);
    if (permissions === "granted" && myAccountId) {
      await fetchFirebaseToken(0);
    }
  };

  // Fetch a firebase token only once the account context has resolved. Without
  // the `myAccountId` guard the device-registration request would be sent with
  // `accountId: undefined`, producing `PUT /accounts/undefined/pushnotificationdevices`
  // (403). We also retry when `myAccountId` arrives so the request can recover
  // from the race where notifications are granted before the account loads.
  useEffect((): void => {
    if (notificationsState === "granted" && !token && myAccountId) {
      fetchFirebaseToken(0);
    }
  }, [notificationsState, myAccountId]);

  // Register the firebase token with our server once we have both the token
  // and an account id. Tracked via `registeredTokenRef` to avoid duplicate
  // calls when re-renders cause the effect to re-evaluate. The ref is updated
  // only after a successful `sendTokenToServer` so a failed request can retry.
  useEffect(() => {
    if (!token || !myAccountId || registeredTokenRef.current === token) {
      return;
    }

    let isActive = true;

    void sendTokenToServer(token)
      .then(() => {
        if (isActive) {
          registeredTokenRef.current = token;
        }
      })
      .catch(() => {
        // Leave ref unset so a later run can retry.
      });

    return () => {
      isActive = false;
    };
  }, [token, myAccountId]);

  /**
   * Display a small modal that lets the user know what just happened when they clicked the below banner
   * @constructor
   */
  const NotificationConfirmationBox = (): React.JSX.Element => {
    return (
      <ModalForm
        open={requestNotification}
        onClose={() => setRequestNotification(() => false)}
        allowClose={false}
        maxWidth="380px"
        maxHeight="160px"
        noPadding
        sx={{ p: 2 }}
      >
        <Box sx={{ display: "flex" }}>
          <Box sx={{ mr: 2, display: "flex", alignItems: "start" }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "0.5rem",
                display: "flex",
                alignItems: "center",
                background: theme.palette.secondary.main,
              }}
            >
              {isEdge ? <NorthIcon /> : <NorthWestIcon />}
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: "16px" }}>Allow notifications</Typography>
            {isEdge ? (
              <Typography sx={{ fontSize: "14px", mb: 2 }} color={theme.palette.neutral.main}>
                To enable notifications on your computer, click “Allow” above.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "14px", mb: 2 }} color={theme.palette.neutral.main}>
                To enable notifications on your computer, click “Allow” above.
              </Typography>
            )}
            <Box>
              <Button
                sx={{
                  width: "auto",
                  borderRadius: "1rem",
                  py: "1px",
                  px: 2,
                  textTransform: "none",
                  background: theme.palette.brand.info.main,
                }}
                onClick={() => setRequestNotification(() => false)}
              >
                Got It!
              </Button>
            </Box>
          </Box>
        </Box>
      </ModalForm>
    );
  };

  /**
   * If the browser can't retrieve a token for some reason, have the user re-start, which should trigger an update
   * @constructor
   */
  const BrowserCantGetTokenModal = (): React.JSX.Element => {
    useEffect(() => {
      if (brokenBrowser) {
        ampli.browserCantGetToken();
      }
    }, []);

    return (
      <BrowserErrorRefresh
        open={brokenBrowser}
        setOpen={() => {
          setTokenCheck(() => 0);
          setBrokenBrowser(() => false);
        }}
      >
        <Typography sx={{ mb: 1 }}>Your browser is having troubles receiving push notifications.</Typography>
        <Typography>Please reload the page to continue.</Typography>
      </BrowserErrorRefresh>
    );
  };

  /**
   * Display a banner for a user to click to request notification access
   * @constructor
   */
  const ActivateFirebaseBanner = (): React.JSX.Element => {
    if (notificationsState === "default") {
      return (
        <>
          {NotificationConfirmationBox()}
          <Stack sx={{ height: "80px" }}>
            <Button
              onClick={requestDesktopNotifications}
              sx={{
                py: 1.25,
                display: "flex",
                "&:hover": { background: theme.palette.brand.info.main },
                alignItems: "center",
                textAlign: "left",
                textTransform: "initial",
                justifyContent: "start",
                background: theme.palette.brand.info.main,
                borderRadius: "1rem",
              }}
            >
              <Box sx={{ flexGrow: 1, pl: 0.5 }}>
                <Typography sx={{ fontSize: "16px", mb: 0.5 }}>Turn on desktop notifications</Typography>
                <Typography sx={{ fontSize: "14px" }}>Get notified of new messages.</Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  background: theme.palette.brand.info.dark,
                }}
              >
                <NotificationsOffIcon />
              </Box>
            </Button>
          </Stack>
        </>
      );
    } else if (notificationsState === "denied") {
      return (
        <Box
          sx={{
            p: 2,
            background: theme.palette.brand.info.dark,
            borderRadius: "0.5rem",
            textAlign: "center",
          }}
        >
          <Typography>Notifications are blocked. Please enable them in your browser settings.</Typography>
        </Box>
      );
    }
  };

  const NotificationBanner = () => {
    if (notificationsState === "granted") {
      return <></>;
    }

    if (notificationsState === "denied") {
      return (
        <Box sx={{ p: 1, mr: 2.5, borderRadius: 1, textAlign: "center", background: theme.palette.brand.info.dark }}>
          <Box sx={{ fontSize: "1.125rem", fontWeight: 700 }}>Notifications are blocked.</Box>
          <Box sx={{ fontSize: "0.875rem" }}>Please enable them in your browser settings.</Box>
        </Box>
      );
    }

    return (
      <Box
        component="button"
        onClick={requestDesktopNotifications}
        sx={{
          cursor: "pointer",
          width: "100%",
          height: "100%",
          mr: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          bgcolor: "transparent",
          color: "inherit",
          p: 0,
        }}
      >
        <Box
          sx={{ bgcolor: "#2563eb", width: "100%", p: 1.25, borderRadius: 1, display: "flex", alignItems: "center" }}
        >
          <Box sx={{ flexGrow: 1, textAlign: "left" }}>
            <Box sx={{ fontSize: "1.125rem", fontWeight: 700 }}>Turn on desktop notifications</Box>
            <Box sx={{ fontSize: "0.75rem" }}>Get notified of new messages.</Box>
          </Box>
          <Box sx={{ bgcolor: "#1d4ed8", p: 1, borderRadius: 1 }}>
            <NotificationsOffIcon />
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (notificationsState !== "granted" || !browserCantGetToken) {
      return;
    }
    if (refresh === true && !token) {
      setTokenCheck((value) => value + 1);
    }
    if (refresh === true && !token && tokenCheck >= 4) {
      setBrokenBrowser(() => true);
    }
    if (token) {
    }
  }, [refresh, tokenCheck, token, notificationsState, browserCantGetToken]);

  const showNotificationsBanner = notificationsState === "default" || notificationsState === "denied";

  return {
    NotificationBanner,
    ActivateFirebaseBanner,
    BrowserCantGetTokenModal,
    deleteActiveToken,
    showNotificationsBanner,
    notificationsState,
    requestDesktopNotifications,
  };
}
